import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaUser, FaCar, FaIdCard, FaPhoneAlt, FaEnvelope, FaCalendarAlt, FaStar, FaRoute, FaWallet, FaRupeeSign } from 'react-icons/fa';
import DriverLayout from '../../layouts/DriverLayout';
import { HiShieldCheck } from 'react-icons/hi';
import { API_URL } from '../../config/apiConfig';
import { getDriverProfile, getBookingHistory, reset } from '../../features/driver/driverSlice';
import { getDriverWallet } from '../../features/wallet/walletSlice';

const DriverProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver, bookingHistory, loading, error } = useSelector((state) => state.driver);
  const { wallet } = useSelector((state) => state.wallet);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyRequested, setHistoryRequested] = useState(false);

  useEffect(() => {
    // First check if we already have driver profile data
    if (driver && driver._id) {
      setLoadingProfile(false);
    } else {
      if (driverAuth) {
        const driverId = (driverAuth._id) || (driverAuth.driver && driverAuth.driver._id) || (driverAuth.id) || (driverAuth.driver && driverAuth.driver.id);
        if (driverId) {
          dispatch(getDriverProfile(driverId));
        } else {
          toast.error('Unable to load driver profile. Please try logging in again.');
          setLoadingProfile(false);
        }
      } else {
        setLoadingProfile(false);
      }
    }

    return () => {
      dispatch(reset());
    };
  }, [dispatch, driverAuth, driver]);

  // Load booking history and wallet when driver profile is loaded
  useEffect(() => {
    if (driver && driver._id && !historyRequested) {
      dispatch(getBookingHistory(driver._id));
      dispatch(getDriverWallet(driver._id));
      setHistoryRequested(true);
    }

    if (bookingHistory) {
      setLoadingHistory(false);
    }
  }, [dispatch, driver, historyRequested, bookingHistory]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(reset());
      setLoadingProfile(false);
    }
    
    // Set loading to false when we get the driver profile
    if (driver && driver._id) {
      setLoadingProfile(false);
    }
  }, [error, dispatch, driver]);

  // Ensure all hooks are declared before any early returns to keep hook order stable
  const [showEditModal, setShowEditModal] = useState(false);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleModel: '',
    vehicleNumber: ''
  });
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (driver) {
      setFormState({
        name: driver.name || '',
        email: driver.email || '',
        phone: driver.phone || '',
        vehicleModel: driver.vehicleModel || '',
        vehicleNumber: driver.vehicleNumber || ''
      });
      setAvatarPreview(driver.profilePhoto || '');
    }
  }, [driver]);

  if (loadingProfile || loading) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          <div className="ml-4 text-gray-700 font-medium">Loading driver profile...</div>
        </div>
      </DriverLayout>
    );
  }

  if (!driver) {
    return (
      <DriverLayout>
        <div className="flex flex-col items-center justify-center h-full">
          <h2 className="text-2xl font-bold mb-4 text-red-600">Profile Not Found</h2>
          <p className="text-gray-300">Please log in to view your profile</p>
        </div>
      </DriverLayout>
    );
  }

  // Get driver name - handle different possible structures
  const getDriverName = () => {
    if (driver && driver.name) return driver.name;
    if (driverAuth && driverAuth.name) return driverAuth.name;
    if (driverAuth && driverAuth.driver && driverAuth.driver.name) return driverAuth.driver.name;
    return 'Driver';
  };

  // Calculate joining date
  const joiningDate = driver && driver.createdAt ? new Date(driver.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : 'Not available';

  // Calculate experience (months since joining)
  const calculateExperience = () => {
    if (!driver || !driver.createdAt) return 0;
    const joinDate = new Date(driver.createdAt);
    const currentDate = new Date();
    const diffInMonths = (currentDate.getFullYear() - joinDate.getFullYear()) * 12 + 
                         (currentDate.getMonth() - joinDate.getMonth());
    return diffInMonths;
  };
  
  const experience = calculateExperience();

  // Get driver availability status
  const isAvailable = driver && driver.isAvailable ? true : false;

  // Get driver rating
  const driverRating = driver && driver.ratings && driver.ratings > 0 ? driver.ratings.toFixed(1) : 'No ratings yet';

  // Render currency with separate symbol and amount for modern look
  const renderCurrency = (amount) => {
    try {
      const parts = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).formatToParts(amount || 0);
      const currency = parts.find(p => p.type === 'currency')?.value || '₹';
      const integer = parts.filter(p => p.type === 'integer' || p.type === 'group').map(p => p.value).join('');
      const fraction = parts.find(p => p.type === 'fraction')?.value ? '.' + parts.find(p => p.type === 'fraction')?.value : '';
      return (
        <div className="flex items-baseline">
          <span className="text-lg text-indigo-600 mr-2">{currency}</span>
          <span className="text-3xl font-extrabold text-gray-900">{integer}{fraction}</span>
        </div>
      );
    } catch (e) {
      return <span className="text-3xl font-extrabold text-gray-900">{formatCurrency(amount)}</span>;
    }
  };

  // Calculate total earnings from bookings (ONLINE ONLY)
  const calculateTotalEarnings = () => {
    if (!bookingHistory || bookingHistory.length === 0) return 0;

    return bookingHistory.reduce((total, booking) => {
      if (booking.status === 'completed') {
        const payment = (booking.paymentMethod || booking.paymentMode || '').toString().toLowerCase();
        const isOnline = payment && payment !== 'cod' && payment !== 'cash';
        if (isOnline) {
          const amount = booking.fare || booking.totalAmount || booking.amount || 0;
          return total + Number(amount);
        }
      }
      return total;
    }, 0);
  };

  const totalEarningsFromBookings = calculateTotalEarnings();

  // Currency formatter
  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

  // Prefer wallet-provided totals for accuracy
  const computedTotalEarnings = (() => {
    let total = wallet?.totalEarnings || wallet?.totalOnlineEarnings || 0;
    if ((!total || total === 0) && wallet?.transactions) {
      total = wallet.transactions
        .filter(t => t && (t.type === 'online_ride_earning' || t.type === 'ride_earning' || t.type === 'earning'))
        .reduce((sum, t) => sum + (t.amount || 0), 0);
    }
    return total || totalEarningsFromBookings;
  })();

  const walletBalance = wallet?.balance || 0;

  // Calculate completed trips count
  const completedTrips = bookingHistory ? bookingHistory.filter(booking => booking.status === 'completed').length : 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const validateForm = () => {
    if (!formState.name || !formState.phone) {
      toast.error('Name and phone are required');
      return false;
    }
    if (!/^\d{10}$/.test(formState.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const token = (driverAuth && driverAuth.token) || localStorage.getItem('driverToken') || localStorage.getItem('token');
      const payload = { ...formState };
      if (avatarFile) {
        // convert to base64
        const toBase64 = (file) => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(file);
        });
        payload.profilePhotoBase64 = await toBase64(avatarFile);
      }

      const res = await axios.put(`${API_URL}/driver/auth/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && res.data.success) {
        toast.success('Profile updated successfully');
        setShowEditModal(false);
        // refresh driver profile
        const driverId = driver._id;
        if (driverId) {
          // refresh driver profile
          dispatch(getDriverProfile(driverId));
        }
      } else {
        toast.error(res.data?.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Profile save error:', err);
      console.error('Profile save response data:', err.response?.data);
      console.error('Profile save request config:', err.config);
      toast.error(err.response?.data?.message || err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DriverLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl overflow-hidden text-gray-900 shadow-md">
          {/* Header/Banner */}
          <div className="h-44 md:h-60 relative bg-gradient-to-r from-white to-indigo-50">
            <div className="absolute bottom-0 left-0 w-full transform translate-y-1/2 px-6 flex flex-col md:flex-row justify-between items-end">
              <div className="flex items-end w-full md:w-auto">
                <div className="bg-white p-1 rounded-full h-28 w-28 md:h-36 md:w-36 flex items-center justify-center shadow-lg border overflow-hidden">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="avatar" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-3xl md:text-4xl font-bold text-gray-700">{getDriverName().charAt(0).toUpperCase()}</span>
                  )}
                </div>

                <div className="ml-6 mb-4 md:mb-6 flex-1 text-gray-900">
                  <div className="flex items-start justify-between w-full gap-6">
                    <div>
                      <h1 className="text-xl md:text-3xl font-bold">{getDriverName()}</h1>
                      <div className="flex items-center mt-2">
                        <div className={`h-3 w-3 rounded-full mr-2 ${isAvailable ? 'bg-green-400' : 'bg-gray-300'}`}></div>
                        <span className={`text-sm md:text-base font-semibold ${isAvailable ? 'text-green-600' : 'text-gray-500'}`}>
                          {isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <button onClick={() => setShowEditModal(true)} className="flex items-center px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5h6M11 9h6M11 13h6M5 5h.01M5 9h.01M5 13h.01" /></svg>
                        Edit
                      </button>

                      <button onClick={() => {
                        // If authenticated as driver, go to change-password (requires current password)
                        if (driverAuth) navigate('/driver/change-password');
                        else navigate('/driver/forgot-password/send-otp');
                      }} className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md">
                        <HiShieldCheck className="h-4 w-4 mr-2" />
                        Change Password
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-row items-center mt-4 md:mt-0 space-x-3">
                <div className="bg-white px-4 py-2 rounded-lg flex items-center shadow">
                  <div className="flex items-center mr-4 text-gray-700">
                    <FaStar className="text-yellow-400 mr-2 text-xl" />
                    <div>
                      <div className="text-lg font-bold text-indigo-700">{driverRating}</div>
                      <div className="text-xs text-gray-500">Rating</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white px-4 py-2 rounded-lg border flex items-center shadow">
                  <div className="mr-3 text-gray-700">
                    <FaRoute />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-indigo-700">{completedTrips}</div>
                    <div className="text-xs text-gray-500">Rides completed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Body */}
          <div className="pt-16 md:pt-20 px-6 pb-8">
            {/* Current Balance */}
            <div className="mb-8">
              <div className="rounded-xl p-6 bg-white shadow-md border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold mb-1 text-gray-900">Current Balance</h2>
                    <p className="text-gray-500 text-sm">My Wallet</p>
                  </div>
                  <div className="flex items-center">
                    <div className="text-right">
                      {renderCurrency(walletBalance)}
                      <p className="text-xs text-gray-500">Available</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-end">
                  <button onClick={() => navigate('/driver/wallet/withdraw')} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md">Withdraw</button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Personal Information */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 border-b pb-3 mb-4">Personal Information</h2>
                <div className="space-y-4">
                      <div className="flex items-center">
                        <FaUser className="text-gray-300 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Full Name</p>
                          <p className="font-medium">{driver.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FaEnvelope className="text-gray-300 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <p className="font-medium">{driver.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FaPhoneAlt className="text-gray-300 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Phone</p>
                          <p className="font-medium">{driver.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FaCalendarAlt className="text-gray-300 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Joined On</p>
                          <p className="font-medium">{joiningDate}</p>
                        </div>
                      </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 border-b pb-3 mb-4">Vehicle Information</h2>
                <div className="space-y-4">
                    <>
                      <div className="flex items-center">
                        <FaCar className="text-gray-300 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Vehicle Model</p>
                          <p className="font-medium">{driver.vehicleModel}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FaCar className="text-gray-300 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Vehicle Type</p>
                          <p className="font-medium">{typeof driver.vehicleType === 'object' ? driver.vehicleType.name : (driver.vehicleType || 'Sedan')}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FaCar className="text-gray-300 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Vehicle Number</p>
                          <p className="font-medium">{driver.vehicleNumber ? driver.vehicleNumber.split('-')[0] : 'N/A'}</p>
                        </div>
                      </div>
                    </>
                </div>
              </div>

              {/* License Information */}
              <div className="bg-white rounded-xl p-6 shadow border border-gray-200 text-gray-900">
                <h2 className="text-xl font-bold text-gray-900 border-b pb-3 mb-4">License Information</h2>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <FaIdCard className="text-gray-300 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">License Number</p>
                      <p className="font-medium text-gray-900">{driver.licenseNumber ? driver.licenseNumber.split('-')[0] : 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <FaCalendarAlt className="text-gray-300 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">License Expiry</p>
                      <p className="font-medium text-gray-900">
                        {driver.licenseExpiry ? new Date(driver.licenseExpiry).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }).replace(/\//g, ' ') : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats/Performance */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 border-b pb-3 mb-4">Performance</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Rating</p>
                    <div className="flex items-center mt-1">
                      <span className="text-2xl font-bold mr-2">{driverRating}</span>
                      <FaStar className="text-yellow-400 text-xl" />
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Trips Completed</p>
                    <p className="text-2xl font-bold">{completedTrips}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Acceptance Rate</p>
                    <p className="text-2xl font-bold">{driver.acceptanceRate || '95'}%</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <p className="text-sm text-gray-500">Experience</p>
                    <p className="text-2xl font-bold">{experience} <span className="text-sm font-normal">months</span></p>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>
      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Profile</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">Close</button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center space-x-4">
                <div className="h-20 w-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center text-2xl text-gray-300">
                  {avatarPreview ? <img src={avatarPreview} alt="avatar" className="h-full w-full object-cover" /> : getDriverName().charAt(0)}
                </div>
                <div>
                  <label className="block text-sm text-gray-300">Upload avatar</label>
                  <input type="file" accept="image/*" onChange={handleAvatarChange} className="mt-1" />
                </div>
              </div>

              <input name="name" value={formState.name} onChange={handleInputChange} className="border border-gray-200 rounded-md p-2" placeholder="Full name" />
              <input name="email" value={formState.email} onChange={handleInputChange} className="border border-gray-200 rounded-md p-2" placeholder="Email" />
              <input name="phone" value={formState.phone} onChange={handleInputChange} className="border border-gray-200 rounded-md p-2" placeholder="Phone" />
              <div className="flex justify-end space-x-2 mt-4">
                <button onClick={() => setShowEditModal(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded">{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </DriverLayout>
  );
};

export default DriverProfile;
